import { LiksDateRangeDialog } from '../../../components/liks/LiksDateRangeDialog/LiksDateRangeDialog';
import { LiksPagination } from '../../../components/liks/LiksPagination/LiksPagination';
import { LacCrawlJobsTimeSeriesChart } from '../../../features/lac/components/LacCrawlJobsTimeSeriesChart';
import { LacTenantCrawlJobTimeSeriesChart } from '../../../features/lac/components/LacTenantCrawlJobTimeSeriesChart';
import { useLacWebsiteCrawlsTab } from '../../../features/lac/hooks/useLacWebsiteCrawlsTab';
import { PAGE_SIZES } from '../../../types/userConfig';
import { LacWebsiteCrawlsJobList } from '../LacWebsiteCrawlsJobList';
import { LacWebsiteCrawlsToolbar } from '../LacWebsiteCrawlsToolbar';
import '../../../styles/resourceStatus.css';
import './LacWebsiteCrawlsTab.css';

export type LacWebsiteCrawlsTabProps = {
  /** When set, list is scoped to this tenant via the API. */
  tenantName?: string;
};

export function LacWebsiteCrawlsTab({
  tenantName,
}: LacWebsiteCrawlsTabProps = {}) {
  const {
    scopedTenantName,
    showTenantFilter,
    tenantsQuery,
    filters,
    filteredTenantName,
    crawlJobsQuery,
    jobs,
    hasPreviousPage,
    hasNextPage,
    areAllExpanded,
    handleManualRefresh,
    expandAllJobs,
    collapseAllJobs,
    toggleJobExpanded,
  } = useLacWebsiteCrawlsTab({ tenantName });

  const { isFetching, isLoading, isError } = crawlJobsQuery;
  const isTenantScoped = scopedTenantName != null;

  const statsChart = isTenantScoped ? (
    <LacTenantCrawlJobTimeSeriesChart tenantName={scopedTenantName} />
  ) : (
    <LacCrawlJobsTimeSeriesChart />
  );

  return (
    <div className="lac-website-crawls lac-website-crawls--with-stats">
      <div className="lac-website-crawls__tab-scroll liks-scrollbar">
        <div className="lac-website-crawls__stats">{statsChart}</div>
        <LacWebsiteCrawlsToolbar
          searchInput={filters.searchInput}
          onSearchChange={filters.handleSearchChange}
          rangeLabel={filters.rangeLabel}
          onOpenDateDialog={() => filters.setIsDateDialogOpen(true)}
          statusFilter={filters.statusFilter}
          onStatusFilterChange={filters.handleStatusFilterChange}
          statusFilterOptions={filters.statusFilterOptions}
          showTenantFilter={showTenantFilter}
          tenantFilter={filters.tenantFilter}
          onTenantFilterChange={filters.handleTenantFilterChange}
          tenantFilterOptions={filters.tenantFilterOptions}
          tenantFilterDisabled={tenantsQuery.isLoading || tenantsQuery.isError}
          onManualRefresh={handleManualRefresh}
          jobsLength={jobs.length}
          areAllExpanded={areAllExpanded}
          onExpandAll={expandAllJobs}
          onCollapseAll={collapseAllJobs}
        />
        <div className="lac-website-crawls__list-host">
          <LacWebsiteCrawlsJobList
            isLoading={isLoading}
            isError={isError}
            isFetching={isFetching}
            jobs={jobs}
            hasActiveFilters={filters.hasActiveFilters}
            page={filters.page}
            expandedJobIds={filters.expandedJobIds}
            onToggleJobExpanded={toggleJobExpanded}
            showTenant={filteredTenantName == null}
          />
        </div>
      </div>
      <LiksDateRangeDialog
        isOpen={filters.isDateDialogOpen}
        fromDay={filters.fromDay}
        toDay={filters.toDay}
        onClose={() => filters.setIsDateDialogOpen(false)}
        onApply={filters.handleApplyDateRange}
      />
      <LiksPagination
        page={filters.page}
        pageSize={filters.pageSize}
        pageSizes={PAGE_SIZES}
        onPageSizeChange={(size) => {
          if (PAGE_SIZES.some((option) => option === size)) {
            filters.handlePageSizeChange(size as (typeof PAGE_SIZES)[number]);
          }
        }}
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        onPreviousPage={() =>
          filters.handlePageChange(Math.max(1, filters.page - 1))
        }
        onNextPage={() => {
          if (hasNextPage) {
            filters.handlePageChange(filters.page + 1);
          }
        }}
      />
    </div>
  );
}
